import React, { useContext } from 'react';
import { AuthContext } from '../App';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Bell, BellOff, AlertTriangle, CheckCircle2, ShieldCheck, Clock, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { usePageHeader } from '../components/Layout';
import { SectionCard } from '../design/primitives';

export default function NotificationSettings() {
  const { user } = useContext(AuthContext);
  const { isSubscribed, isLoading, error, permission, subscribe, unsubscribe } = usePushNotifications(user);

  usePageHeader([{ label: 'Bildirim Ayarları' }], null, []);

  const unsupported = !!(error && error.includes('desteklemiyor'));
  const denied = permission === 'denied';

  const handleToggle = async () => {
    const result = isSubscribed ? await unsubscribe() : await subscribe();
    if (result.success) toast.success(result.message);
    else toast.error(result.message);
  };

  if (!user) {
    return (
      <div className="p-4 sm:p-6">
        <div className="ox-surface p-8 rounded-md text-center ox-text-muted">
          Giriş yapmanız gerekiyor
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-4xl mx-auto space-y-4">
      {unsupported && (
        <div className="rounded-md p-4 flex items-start gap-3" style={{ background: 'var(--warn-weak)', border: '1px solid var(--warn)' }}>
          <AlertTriangle size={18} style={{ color: 'var(--warn)' }} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium">Tarayıcı Uyarısı</div>
            <div className="text-sm ox-text-muted mt-1">{error}</div>
          </div>
        </div>
      )}

      {denied && (
        <div className="rounded-md p-4 flex items-start gap-3" style={{ background: 'var(--danger-weak)', border: '1px solid var(--danger)' }}>
          <AlertTriangle size={18} style={{ color: 'var(--danger)' }} className="mt-0.5 flex-shrink-0" />
          <div>
            <div className="text-sm font-medium">Bildirim İzni Engellendi</div>
            <div className="text-sm ox-text-muted mt-1">
              Tarayıcınızın ayarlarından OnxFlow için bildirimlere izin vermeniz gerekiyor.
            </div>
          </div>
        </div>
      )}

      <SectionCard
        title="Push Bildirimler"
        subtitle={isSubscribed ? 'Aktif — bildirimler alıyorsunuz' : 'Devre dışı'}
        right={
          <button
            onClick={handleToggle}
            disabled={isLoading || denied || unsupported}
            className={isSubscribed ? 'ox-btn' : 'ox-btn ox-btn-primary'}
            style={isSubscribed ? { color: 'var(--danger)', borderColor: 'var(--danger)' } : {}}
          >
            {isLoading ? (
              'İşlem yapılıyor...'
            ) : isSubscribed ? (
              <><BellOff size={14} style={{ marginRight: 6 }} />Devre Dışı Bırak</>
            ) : (
              <><Bell size={14} style={{ marginRight: 6 }} />Aktif Et</>
            )}
          </button>
        }
      >
        <div className="p-5 flex items-center gap-4">
          <div
            className="flex items-center justify-center rounded-full"
            style={{
              width: 48,
              height: 48,
              background: isSubscribed ? 'var(--good-weak)' : 'var(--surface-2)',
              color: isSubscribed ? 'var(--good)' : 'var(--text-muted)',
              border: '1px solid var(--border)',
            }}
          >
            {isSubscribed ? <Bell size={22} /> : <BellOff size={22} />}
          </div>
          <div className="flex-1">
            <div className="text-sm font-medium">
              {isSubscribed ? 'Bu cihaz aboneliklerinizde kayıtlı' : 'Bu cihazda bildirimler devre dışı'}
            </div>
            <div className="text-xs ox-text-muted mt-1">
              {isSubscribed
                ? 'Görevler, yorumlar ve önemli güncellemeler cihazınıza gönderilir.'
                : 'Gerçek zamanlı güncellemeleri almak için aktif edin.'}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Bildirimler Hakkında">
        <div className="p-5 space-y-4">
          <InfoRow
            icon={<Zap size={16} />}
            title="Gerçek Zamanlı Güncellemeler"
            text="Size atanan yeni görevler, yapılan yorumlar ve önemli güncellemeler hakkında anında bildirim alın."
          />
          <InfoRow
            icon={<Clock size={16} />}
            title="Çalışma Saatleri Dışında"
            text="Tarayıcı kapalıyken bile önemli bildirimler cihazınıza ulaşır."
          />
          <InfoRow
            icon={<ShieldCheck size={16} />}
            title="Gizlilik ve Güvenlik"
            text="Bildirimleriniz şifreli olarak gönderilir ve sadece sizin cihazınızda görüntülenir."
          />
        </div>
      </SectionCard>

      <SectionCard title="Teknik Detaylar">
        <div className="p-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
          <DetailItem
            label="Durum"
            value={isSubscribed ? 'Aktif' : 'Pasif'}
            tone={isSubscribed ? 'good' : 'muted'}
          />
          <DetailItem
            label="İzin"
            value={permission === 'granted' ? 'Verildi' : permission === 'denied' ? 'Reddedildi' : 'Bekleniyor'}
            tone={permission === 'granted' ? 'good' : permission === 'denied' ? 'danger' : 'muted'}
          />
          <DetailItem
            label="Tarayıcı Desteği"
            value={'serviceWorker' in navigator && 'PushManager' in window ? 'Destekleniyor' : 'Desteklenmiyor'}
            tone={'serviceWorker' in navigator && 'PushManager' in window ? 'good' : 'danger'}
          />
        </div>
      </SectionCard>

      <div className="rounded-md p-4 flex items-start gap-3" style={{ background: 'var(--accent-weak)', border: '1px solid var(--accent)' }}>
        <CheckCircle2 size={18} style={{ color: 'var(--accent)' }} className="mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-sm font-medium">Yardım</div>
          <div className="text-sm ox-text-muted mt-1">
            Bildirimleri aktif etmekte sorun yaşıyorsanız, tarayıcınızın ayarlarından OnxFlow için bildirimlere izin verdiğinizden emin olun.
          </div>
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon, title, text }) {
  return (
    <div className="flex items-start gap-3">
      <div
        className="flex items-center justify-center rounded-md flex-shrink-0"
        style={{
          width: 28,
          height: 28,
          background: 'var(--accent-weak)',
          color: 'var(--accent)',
        }}
      >
        {icon}
      </div>
      <div className="flex-1">
        <div className="text-sm font-medium">{title}</div>
        <div className="text-xs ox-text-muted mt-0.5">{text}</div>
      </div>
    </div>
  );
}

function DetailItem({ label, value, tone }) {
  const colorMap = {
    good: 'var(--good)',
    danger: 'var(--danger)',
    muted: 'var(--text-muted)',
  };
  return (
    <div className="ox-surface-muted rounded-md p-3">
      <div className="text-[11px] uppercase tracking-wide ox-text-dim">{label}</div>
      <div className="text-sm font-medium mt-1" style={{ color: colorMap[tone] || 'var(--text)' }}>
        {value}
      </div>
    </div>
  );
}
